import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ContentState } from 'draft-js';
import type { LanguageToolMatch } from '../../services/LanguageTool';
import { findMatchForDecoration, matchToBlockRange } from './languageToolOffsets';

export type GetMatches = () => LanguageToolMatch[];

export type LanguageToolDecoratorProps = {
    children: React.ReactNode;
    decoratedText: string;
    start: number;
    end: number;
    blockKey: string;
    contentState: ContentState;
    getMatches: GetMatches;
    onApplySuggestion: (match: LanguageToolMatch, replacement: string) => void;
};

type PopupCoords = {
    top: number;
    left: number;
};

/**
 * LanguageTool underline + suggestion menu.
 *
 * The menu is portaled to document.body so it never injects sibling DOM into
 * Draft's contenteditable. Extra nodes there trigger DOMObserver mutations
 * without a data-offset-key ancestor and crash via nullthrows.
 */
const LanguageToolDecoration = (props: LanguageToolDecoratorProps) => {
    const {
        children, start, end, blockKey, contentState, getMatches, onApplySuggestion,
    } = props;

    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<PopupCoords | null>(null);
    const rootRef = useRef<HTMLSpanElement>(null);
    const popupRef = useRef<HTMLSpanElement>(null);

    const match = findMatchForDecoration(contentState, getMatches(), blockKey, start, end);
    const replacements = (match?.replacements || []).slice(0, 5);
    const message = match?.shortMessage || match?.message || '';

    useLayoutEffect(() => {
        if (!open || !rootRef.current) {
            setCoords(null);
            return undefined;
        }

        const update = () => {
            const rect = rootRef.current?.getBoundingClientRect();
            if (!rect)
                return;
            setCoords({ top: rect.bottom + 4, left: rect.left });
        };

        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => {
        if (!open)
            return undefined;

        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (rootRef.current?.contains(target))
                return;
            if (popupRef.current?.contains(target))
                return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    if (!match)
        return <span>{children}</span>;

    const popup = open && coords && typeof document !== 'undefined'
        ? createPortal(
            <span
                ref={popupRef}
                className="fixed z-50 min-w-[12rem] max-w-xs rounded-md border border-gray-200 bg-white p-2 text-left text-xs shadow-lg dark:border-gray-600 dark:bg-gray-800"
                style={{ top: coords.top, left: coords.left }}
                role="dialog"
                onMouseDown={(e) => {
                    // Keep editor selection when interacting with the popup.
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                {message && (
                    <span className="mb-1 block font-medium text-gray-800 dark:text-gray-100">
                        {message}
                    </span>
                )}
                {replacements.length === 0 ? (
                    <span className="block text-gray-500 dark:text-gray-400">—</span>
                ) : (
                    replacements.map((replacement) => (
                        <button
                            key={replacement.value}
                            type="button"
                            className="mt-1 block w-full rounded px-2 py-1 text-left text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-gray-700"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onApplySuggestion(match, replacement.value);
                                setOpen(false);
                            }}
                        >
                            {replacement.value}
                        </button>
                    ))
                )}
            </span>,
            document.body,
        )
        : null;

    // Single DOM root required by Draft decorators; portal renders outside
    // contenteditable so MutationObserver never sees the suggestion menu.
    return (
        <span
            ref={rootRef}
            className="cursor-pointer border-b-2 border-dotted border-red-500 dark:border-red-400"
            title={message}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
            }}
        >
            {children}
            {popup}
        </span>
    );
};

export const createLanguageToolDecorators = (
    getMatches: GetMatches,
    onApplySuggestion: (match: LanguageToolMatch, replacement: string) => void,
) => {
    return [{
        strategy: (
            contentBlock: any,
            callback: (start: number, end: number) => void,
            contentState: ContentState,
        ) => {
            const blockKey = contentBlock.getKey();
            const matches = getMatches();
            if (!matches.length)
                return;
            for (const match of matches) {
                const range = matchToBlockRange(contentState, match, blockKey);
                if (range)
                    callback(range.start, range.end);
            }
        },
        component: (decoratorProps: any) => (
            <LanguageToolDecoration
                {...decoratorProps}
                getMatches={getMatches}
                onApplySuggestion={onApplySuggestion}
            />
        ),
    }];
};

export default LanguageToolDecoration;
